<template>
  <div class="sv" :class="{ 'sv--immersive': immersive }" dir="rtl">

    <!-- ── Top overlay: avatar + search bar ── -->
    <div class="sv__top">
    <router-link to="/account" class="sv__avatar" title="حسابي" aria-label="حسابي">
      <i v-if="!hasUser" class="fa fa-user"></i>
      <template v-else>{{ userInitial }}</template>
      <span v-if="unreadCount > 0" class="sv__avatar-dot"></span>
    </router-link>

      <div class="sv__search" :class="{ 'sv__search--focus': searchFocused }">
        <i class="fa fa-search sv__search-icon"></i>
        <input
          v-model="query"
          type="search"
          class="sv__search-input"
          placeholder="ابحث عن صنف..."
          autocomplete="off"
          autocorrect="off"
          spellcheck="false"
          @input="onSearchInput"
          @focus="searchFocused = true"
          @blur="searchFocused = false"
        />
        <button
          v-if="query"
          type="button"
          class="sv__search-clear"
          @click="clearSearch"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Category pill — shows the active category, opens the picker -->
      <button
        v-if="categories.length"
        type="button"
        class="sv__cat-btn"
        :class="{ 'sv__cat-btn--active': activeCategory }"
        @click="catOpen = !catOpen"
      >
        <span class="sv__cat-btn-label">{{ activeCategory || 'الكل' }}</span>
        <i class="fa fa-chevron-down sv__cat-btn-caret" :class="{ 'sv__cat-btn-caret--open': catOpen }"></i>
      </button>
    </div>

    <!-- ── Category picker (dropdown panel under the top bar) ── -->
    <div v-if="catOpen" class="sv__cat-backdrop" @click="catOpen = false"></div>
    <Transition name="cat-fade">
      <div v-if="catOpen" class="sv__cat-panel">
        <button
          class="sv__chip" :class="{ 'sv__chip--on': !activeCategory }"
          @click="selectCategory('')" type="button"
        >الكل</button>
        <button
          v-for="cat in categories"
          :key="cat.name"
          class="sv__chip"
          :class="{ 'sv__chip--on': activeCategory === cat.name, 'sv__chip--off': !cat.has_results }"
          :disabled="!cat.has_results && activeCategory !== cat.name"
          @click="selectCategory(cat.name)"
          type="button"
        >{{ cat.name }}</button>
      </div>
    </Transition>

    <!-- ── Vertical snap feed ── -->
    <div class="sv__feed" ref="feedEl" @scroll.passive="onScroll">

      <!-- Each card = one screen with a full-bleed photo and overlay info -->
      <article
        v-for="(card, idx) in cards"
        :key="card.key"
        class="sv__card"
        :data-idx="idx"
      >
        <!-- Image zone: tap opens fullscreen lightbox -->
        <div class="sv__card-image" @click="onImageTap(card)">
          <div v-if="!loadedKeys.has(card.key)" class="sv__card-skel">
            <i class="fa fa-spinner fa-spin"></i>
          </div>
          <img
            class="sv__card-img"
            :src="card.image_url"
            :alt="card.item_name"
            decoding="async"
            @load="markLoaded(card.key)"
            @error="onImgError($event, card.key)"
          />
        </div>

        <!-- Bottom gradient + info — tap opens BranchDetails -->
        <div class="sv__card-overlay">
          <div class="sv__card-info" @click="openBranch(card)">

            <div class="sv__card-head">
              <h3 class="sv__card-name">{{ card.item_name }}</h3>
              <div class="sv__card-meta">
                <button
                  type="button"
                  class="sv__card-like"
                  :class="{ 'sv__card-like--on': card.liked_by_me }"
                  @click.stop="toggleLike(card)"
                >
                  <i class="fa fa-heart"></i>
                  <span v-if="card.likes_count">{{ card.likes_count }}</span>
                </button>
                <button
                  type="button"
                  class="sv__card-flag"
                  @click.stop="openReport(card)"
                  aria-label="إبلاغ"
                  title="إبلاغ عن هذا المحتوى"
                >
                  <i class="fa fa-flag"></i>
                </button>
                <span
                  v-if="card.visit_type"
                  class="sv__card-visit"
                  :class="`sv__card-visit--${card.visit_type}`"
                >
                  <i :class="visitIcon(card.visit_type)"></i>
                  {{ visitLabel(card.visit_type) }}
                </span>
                <span v-if="card.rating" class="sv__card-rating">
                  <i class="fa fa-star"></i>
                  {{ card.rating }}
                </span>
                <span v-if="card.price != null" class="sv__card-price">
                  {{ card.price }} ر.س
                </span>
              </div>
            </div>

            <p
              v-if="card.review"
              class="sv__card-review"
              :class="{ 'sv__card-review--open': expandedKeys.has(card.key) }"
              @click.stop="toggleReview(card.key)"
            >
              {{ card.review }}
              <span v-if="card.user_name" class="sv__card-by">— {{ card.user_name }}</span>
            </p>

            <div v-if="card.branch_name || card.branch_district" class="sv__card-place">
              <i class="fa fa-map-marker"></i>
              <span v-if="card.branch_name" class="sv__card-place-name">
                {{ card.branch_name }}
              </span>
              <span v-if="card.branch_district || card.branch_city" class="sv__card-place-loc">
                {{ [card.branch_district, card.branch_city].filter(Boolean).join(' · ') }}
              </span>
              <i class="fa fa-chevron-left sv__card-place-arrow"></i>
            </div>

          </div>
        </div>
      </article>

      <!-- ── Footer states ── -->
      <div v-if="loading && !cards.length" class="sv__state">
        <i class="fa fa-spinner fa-spin"></i>
        <span>جاري التحميل...</span>
      </div>

      <div v-else-if="!loading && !cards.length" class="sv__state sv__state--empty">
        <div class="sv__state-icon">🍽️</div>
        <p>{{ searchActive ? 'لا توجد نتائج' : 'لا توجد أصناف بصور بعد' }}</p>
        <span v-if="!searchActive">كن أول من يشارك صورة لطبقك المفضل!</span>
      </div>

      <div v-else-if="loadingMore" class="sv__state sv__state--more">
        <i class="fa fa-spinner fa-spin"></i>
      </div>

    </div>

    <!-- ── Report sheet ── -->
    <ReportSheet
      :open="report.open"
      target-type="menu_vote"
      :target-id="report.id"
      @close="report.open = false"
      @gone="removeCardById(report.id)"
    />

    <!-- ── Fullscreen image lightbox ── -->
    <Teleport to="body">
      <Transition name="lb-fade">
        <div
          v-if="lightboxImg"
          class="sv__lb"
          @click="closeLightbox"
          @touchstart.passive="lbTouchStart"
          @touchmove.prevent="lbTouchMove"
          @touchend.passive="lbTouchEnd"
        >
          <button class="sv__lb-close" type="button" @click.stop="closeLightbox">
            <i class="fa fa-times"></i>
          </button>
          <img
            class="sv__lb-img"
            :class="{ 'sv__lb-img--dragging': lbDragging }"
            :src="lightboxImg"
            :style="{ transform: `translate(${lbPanX}px, ${lbPanY}px) scale(${lbZoom})` }"
            @click.stop
            draggable="false"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { api } from "@/services/api";
import { useNotifications } from "@/composables/useNotifications";
import ReportSheet from "@/components/ReportSheet.vue";


const router    = useRouter();
const apiUrlImg = process.env.VUE_APP_API_URL_IMAGES || "";
const fallback  = `${apiUrlImg}/branches/defaultBranch.jpeg`;

// ── State ─────────────────────────────────────
const cards         = ref([]);          // normalized cards (feed votes OR search results)
const query         = ref("");
const searchFocused = ref(false);
const searchActive  = ref(false);
const loading       = ref(false);       // initial / search loading
const loadingMore   = ref(false);       // pagination loading
const page          = ref(1);
const hasMore       = ref(true);

// Category filter (pill + dropdown picker)
const categories     = ref([]);   // [{ name, has_results }]
const activeCategory = ref("");   // active item_category, or "" for all
const catOpen        = ref(false);

// Immersive mode — tap photo to hide all chrome (TikTok-style)
const immersive = ref(false);
let tapTimer = null;
function onImageTap(card) {
  if (tapTimer) {
    // second tap within the window → treat as double tap → zoom
    clearTimeout(tapTimer);
    tapTimer = null;
    openLightbox(card.image_url);
  } else {
    tapTimer = setTimeout(() => {
      tapTimer = null;
      immersive.value = !immersive.value;
    }, 220);
  }
}

// Per-card load state — keys whose images have finished decoding
const loadedKeys = ref(new Set());
function markLoaded(key) { loadedKeys.value.add(key); loadedKeys.value = new Set(loadedKeys.value); }

// Per-card expanded review — tap the review to toggle 2-line clamp / full text
const expandedKeys = ref(new Set());
function toggleReview(key) {
  const next = new Set(expandedKeys.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expandedKeys.value = next;
}

const feedEl = ref(null);

// IntersectionObserver — preload next 2 images when a card scrolls into view
let cardObserver = null;
function preloadAt(idx) {
  for (let i = idx + 1; i <= idx + 2; i++) {
    const c = cards.value[i];
    if (c?.image_url && !loadedKeys.value.has(c.key)) {
      const img = new Image();
      img.decoding = "async";
      img.src = c.image_url;
    }
  }
}
// Notifications 
const { unreadCount, fetchUnread } = useNotifications();
onMounted(() => fetchUnread());

function setupCardObserver() {
  teardownCardObserver();
  if (typeof IntersectionObserver === "undefined" || !feedEl.value) return;
  cardObserver = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting && e.intersectionRatio > 0.5) {
        const idx = Number(e.target.dataset.idx);
        if (Number.isFinite(idx)) preloadAt(idx);
      }
    }
  }, { root: feedEl.value, threshold: [0.5] });

  // Observe all current card elements
  nextTick(() => {
    document.querySelectorAll(".sv__card").forEach(el => cardObserver.observe(el));
  });
}

function teardownCardObserver() {
  if (cardObserver) { cardObserver.disconnect(); cardObserver = null; }
}

// Guests get a user icon instead of an initial
const cachedUser = (() => {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
})();
const hasUser     = computed(() => !!cachedUser?.name);
const userInitial = computed(() => cachedUser?.name?.charAt(0)?.toUpperCase() || "");

// ── Normalize a vote card (feed AND search share this shape now) ──
function normalizeCard(vote) {
  const item   = vote.menu_item || {};
  const branch = item.branch     || {};
  return {
    key:             `v${vote.id}`,
    id:              vote.id,     // menu vote id — used for the like toggle
    likes_count:     vote.likes_count ?? 0,
    liked_by_me:     !!vote.liked_by_me,
    image_url:       vote.image_url,
    item_id:         item.id,
    item_name:       item.name,
    item_category:   item.item_category,
    rating:          vote.rating,    // user's rating on this photo
    price:           vote.price,     // price the user paid (whole number or null)
    review:          vote.review,    // user's review text
    visit_type:      vote.visit_type, // 'visit' | 'pickup' | 'delivery' | null
    user_name:       vote.user_name,
    branch_id:       branch.id,
    branch_name:     branch.name,
    branch_district: branch.district,
    branch_city:     branch.city,
  };
}

// ── Feed (latest votes with images; optional category) ──
async function loadFeed(p = 1) {
  if (p === 1) loading.value = true;
  else         loadingMore.value = true;

  try {
    const params = { page: p };
    if (activeCategory.value) params.category = activeCategory.value;
    // api client sends the Bearer token when logged in → fills liked_by_me
    const res = await api.get("/menu-items/feed", { params });
    const data = res.data || {};
    const items = data.data ?? [];
    const newCards = items.map(normalizeCard);

    if (p === 1) {
      cards.value = newCards;
      if (Array.isArray(data.categories)) categories.value = data.categories;
    } else {
      const seen = new Set(cards.value.map(c => c.key));
      cards.value = [...cards.value, ...newCards.filter(c => !seen.has(c.key))];
    }

    page.value    = data.current_page ?? p;
    hasMore.value = (data.current_page ?? p) < (data.last_page ?? p);

    setupCardObserver();
    preloadAt(-1);
  } catch (e) {
    console.error("Feed load error:", e);
    if (p === 1) cards.value = [];
    hasMore.value = false;
  } finally {
    loading.value      = false;
    loadingMore.value  = false;
  }
}

// ── Search (debounced) ────────────────────────
let searchTimer = null;

// Route to the right endpoint based on whether there's a search term.
// q >= 2  → search (q + optional category)
// q < 2   → feed (optional category) — category-only browsing or full feed
function runQuery() {
  page.value = 1;
  const q = query.value.trim();
  if (q.length >= 2) {
    doSearch(1);
  } else {
    searchActive.value = false;
    loadFeed(1);
    scrollToTop();
  }
}

function onSearchInput() {
  clearTimeout(searchTimer);
  const q = query.value.trim();
  loading.value = true;
  // shorter debounce when clearing back to feed, longer while typing a query
  searchTimer = setTimeout(runQuery, q.length >= 2 ? 400 : 200);
}

function searchParams(p) {
  const params = { page: p };
  const q = query.value.trim();
  if (q.length >= 2) params.q = q;
  if (activeCategory.value) params.category = activeCategory.value;
  return params;
}

async function doSearch(p = 1) {
  searchActive.value = true;
  if (p === 1) loading.value = true;
  else         loadingMore.value = true;

  try {
    const res = await api.get("/menu-items/search", { params: searchParams(p) });
    const data = res.data || {};
    const items = (data.data ?? []).filter(it => it.image_url);
    const newCards = items.map(normalizeCard);

    if (p === 1) {
      cards.value = newCards;
      if (Array.isArray(data.categories)) categories.value = data.categories;
      scrollToTop();
    } else {
      const seen = new Set(cards.value.map(c => c.key));
      cards.value = [...cards.value, ...newCards.filter(c => !seen.has(c.key))];
    }

    page.value    = data.current_page ?? p;
    hasMore.value = (data.current_page ?? p) < (data.last_page ?? p);

    setupCardObserver();
    preloadAt(-1);
  } catch (e) {
    console.error("Search error:", e);
    if (p === 1) cards.value = [];
    hasMore.value = false;
  } finally {
    loading.value     = false;
    loadingMore.value = false;
  }
}

// ── Category chip tap ─────────────────────────
function selectCategory(name) {
  // toggle off if tapping the active one
  activeCategory.value = (activeCategory.value === name) ? "" : name;
  catOpen.value = false;
  runQuery();
}

function clearSearch() {
  clearTimeout(searchTimer);
  query.value = "";
  runQuery(); // back to feed; keeps an active category filter
}

// ── Pagination on scroll (feed or search) ─────
function onScroll() {
  if (loadingMore.value || !hasMore.value) return;
  const el = feedEl.value;
  if (!el) return;
  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
  if (distanceFromBottom < 600) {
    if (searchActive.value) doSearch(page.value + 1);
    else                    loadFeed(page.value + 1);
  }
}

function scrollToTop() {
  nextTick(() => {
    if (feedEl.value) feedEl.value.scrollTop = 0;
  });
}

// ── Like toggle (optimistic; server response wins) ──
async function toggleLike(card) {
  if (!localStorage.getItem("token")) {
    router.push({ name: "Account" });
    return;
  }
  const wasLiked = card.liked_by_me;
  card.liked_by_me = !wasLiked;
  card.likes_count = Math.max(0, card.likes_count + (wasLiked ? -1 : 1));
  try {
    const res = await api.post(`/menu-votes/${card.id}/like`);
    card.liked_by_me = !!res.data?.liked;
    card.likes_count = res.data?.likes_count ?? card.likes_count;
  } catch (e) {
    card.liked_by_me = wasLiked;
    card.likes_count = Math.max(0, card.likes_count + (wasLiked ? 1 : -1));
    if (e?.response?.status === 401) router.push({ name: "Account" });
    else if (e?.response?.status === 404) {
      // Card content was hidden/removed — drop it from the feed
      cards.value = cards.value.filter(c => c.key !== card.key);
    }
  }
}

// ── Report a card (menu vote) ─────────────────
const report = reactive({ open: false, id: null });

function openReport(card) {
  if (!localStorage.getItem("token")) { router.push({ name: "Account" }); return; }
  report.id   = card.id;
  report.open = true;
}

function removeCardById(id) {
  cards.value = cards.value.filter(c => c.id !== id);
}

// ── Open the place (branch info zone tap) ────
function openBranch(card) {
  if (!card.branch_id) return;
  router.push({
    name:   "BranchDetails",
    params: { id: card.branch_id },
    query:  { entrySource: "feed", item_id: card.item_id },
  });
}

// ── Visit-type display (visit | pickup | delivery) ──
function visitIcon(t) {
  if (t === "visit")    return "fa fa-utensils";
  if (t === "pickup")   return "fa fa-bag-shopping";
  if (t === "delivery") return "fa fa-motorcycle";
  return "";
}
function visitLabel(t) {
  if (t === "visit")    return "زيارة";
  if (t === "pickup")   return "استلام";
  if (t === "delivery") return "توصيل";
  return "";
}

function onImgError(e, key) {
  e.target.src = fallback;
  if (key) markLoaded(key); // count fallback as loaded so spinner clears
}

// ── Lightbox (fullscreen with pinch-zoom + pan) ──
const lightboxImg = ref(null);
const lbZoom      = ref(1);
const lbPanX      = ref(0);
const lbPanY      = ref(0);
const lbDragging  = ref(false);

let lbInitialDist = 0;
let lbStartZoom   = 1;
let lbStartPanX   = 0;
let lbStartPanY   = 0;
let lbTouchX      = 0;
let lbTouchY      = 0;
let lbLastTap     = 0;

function openLightbox(url) {
  if (!url) return;
  lightboxImg.value = url;
  lbZoom.value = 1; lbPanX.value = 0; lbPanY.value = 0;
}

function closeLightbox() {
  lightboxImg.value = null;
}

function touchDist(touches) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

function lbTouchStart(e) {
  if (e.touches.length === 2) {
    lbInitialDist = touchDist(e.touches);
    lbStartZoom   = lbZoom.value;
    lbDragging.value = true;
  } else if (e.touches.length === 1) {
    lbTouchX     = e.touches[0].clientX;
    lbTouchY     = e.touches[0].clientY;
    lbStartPanX  = lbPanX.value;
    lbStartPanY  = lbPanY.value;

    // Double-tap to toggle between 1× and 2× zoom
    const now = Date.now();
    if (now - lbLastTap < 300) {
      if (lbZoom.value > 1) {
        lbZoom.value = 1; lbPanX.value = 0; lbPanY.value = 0;
      } else {
        lbZoom.value = 2;
      }
    }
    lbLastTap = now;
  }
}

function lbTouchMove(e) {
  if (e.touches.length === 2) {
    const d = touchDist(e.touches);
    lbZoom.value = Math.max(1, Math.min(4, lbStartZoom * (d / lbInitialDist)));
  } else if (e.touches.length === 1 && lbZoom.value > 1) {
    lbDragging.value = true;
    lbPanX.value = lbStartPanX + (e.touches[0].clientX - lbTouchX);
    lbPanY.value = lbStartPanY + (e.touches[0].clientY - lbTouchY);
  }
}

function lbTouchEnd() {
  lbDragging.value = false;
  // Snap back if zoomed out
  if (lbZoom.value < 1.05) {
    lbZoom.value = 1; lbPanX.value = 0; lbPanY.value = 0;
  }
}

// ── Footer height — sync to CSS var so info card sits above it ──
let footerObserver = null;
function syncFooterHeight() {
  const footerEl = document.querySelector(".fixed-bottom");
  const svEl     = feedEl.value?.parentElement; // .sv
  if (!footerEl || !svEl) return;
  const h = footerEl.offsetHeight || 70;
  svEl.style.setProperty("--app-footer-h", `${h}px`);
}

// ── Lifecycle ─────────────────────────────────
onMounted(() => {
  loadFeed(1);

  // Footer height sync (same trick as HomeMapView)
  syncFooterHeight();
  const footerEl = document.querySelector(".fixed-bottom");
  if (footerEl && typeof ResizeObserver !== "undefined") {
    footerObserver = new ResizeObserver(syncFooterHeight);
    footerObserver.observe(footerEl);
  }
  window.addEventListener("resize", syncFooterHeight);
});

onUnmounted(() => {
  clearTimeout(searchTimer);
  teardownCardObserver();
  if (footerObserver) footerObserver.disconnect();
  window.removeEventListener("resize", syncFooterHeight);
});
</script>

<style scoped lang="scss">
.sv {
  position: fixed;
  top: 0;
  left: var(--app-gutter);
  right: var(--app-gutter);
  bottom: var(--app-footer-h, 70px);
  overflow: hidden;
  background: #000;
  direction: rtl;
  z-index: 1; // below AppFooter (z:1030), above page content
}

// ── Top overlay (avatar + search) ─────────────
.sv__top {
  // Standalone/PWA on iPhone: the view starts under the status bar + notch,
  // so the search bar must clear the top safe area or it sits behind the camera.
  position: absolute; top: max(12px, env(safe-area-inset-top, 12px)); left: 12px; right: 12px;
  z-index: 11;
  display: flex; align-items: center; gap: 8px;
  direction: rtl;
  transition: opacity 0.25s ease, transform 0.25s ease;
}

// Immersive mode — hide all chrome, leave just the photo
.sv--immersive {
  .sv__top    { opacity: 0; transform: translateY(-8px); pointer-events: none; }
  .sv__cat-panel, .sv__cat-backdrop { opacity: 0; pointer-events: none; }
  .sv__card-overlay { opacity: 0; pointer-events: none; }
}

// Profile button — solid disc + gold ring so it reads as a button over photos
// (same look as the map view's avatar)
.sv__avatar {
  flex-shrink: 0;
  width: 40px; height: 40px;
  border-radius: 50%;
  background: rgba(26, 22, 18, 0.96);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1.5px solid rgba(226, 182, 115, 0.70);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
  color: var(--color-signal);
  display: flex; align-items: center; justify-content: center;
  font-family: var(--font-display);
  font-weight: 700; font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.15s;
  i { font-size: 0.85rem; }
  &:hover, &:active {
    border-color: var(--color-signal);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45), 0 0 0 3px var(--color-signal-soft);
  }
}

.sv__search {
  flex: 1; min-width: 0;
  display: flex; align-items: center; gap: 6px;
  height: 40px; padding: 0 12px;
  background: rgba(26, 22, 18, 0.30);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(247, 241, 230, 0.18);
  border-radius: var(--radius-full);
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;

  &--focus {
    background: rgba(26, 22, 18, 0.92);
    border-color: var(--color-signal);
    box-shadow: 0 0 0 3px var(--color-signal-soft);
  }
}

.sv__search-icon {
  color: rgba(247, 241, 230, 0.70);
  font-size: 0.85rem; flex-shrink: 0;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}

.sv__search-input {
  flex: 1; min-width: 0;
  background: transparent; border: none; outline: none;
  color: #F7F1E6; font-family: var(--font-primary);
  font-size: 0.85rem;
  direction: rtl;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  &::placeholder {
    color: rgba(247, 241, 230, 0.60);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  }
  &::-webkit-search-cancel-button { display: none; }
}

.sv__search-clear {
  flex-shrink: 0;
  width: 22px; height: 22px; border-radius: 50%;
  border: none; background: rgba(247, 241, 230, 0.10);
  color: rgba(247, 241, 230, 0.65);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.65rem;
  &:hover { background: rgba(247, 241, 230, 0.18); color: #F7F1E6; }
}

// ── Category pill (in the top bar, next to search) ──
.sv__cat-btn {
  flex-shrink: 0;
  display: flex; align-items: center; gap: 6px;
  height: 40px; padding: 0 14px;
  max-width: 130px;
  background: rgba(26, 22, 18, 0.30);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(247, 241, 230, 0.18);
  border-radius: var(--radius-full);
  color: #F7F1E6;
  font-family: var(--font-primary);
  font-size: 0.8rem; font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &--active {
    background: var(--color-signal, #E2B673);
    border-color: var(--color-signal, #E2B673);
    color: #1a1410;
  }
}

.sv__cat-btn-label {
  min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.sv__cat-btn-caret {
  flex-shrink: 0;
  font-size: 0.6rem;
  transition: transform 0.2s ease;
  &--open { transform: rotate(180deg); }
}

// ── Category picker panel + backdrop ──
.sv__cat-backdrop {
  position: absolute; inset: 0;
  z-index: 12;
}

.sv__cat-panel {
  // 48px below .sv__top (40px bar + gap), tracking the same safe-area offset
  position: absolute; top: calc(max(12px, env(safe-area-inset-top, 12px)) + 48px); left: 12px; right: 12px;
  z-index: 13;
  display: flex; flex-wrap: wrap; gap: 8px;
  direction: rtl;
  padding: 12px;
  max-height: 50%;
  overflow-y: auto;
  background: rgba(26, 22, 18, 0.92);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(247, 241, 230, 0.16);
  border-radius: var(--radius-md);
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

.cat-fade-enter-active,
.cat-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.cat-fade-enter-from,
.cat-fade-leave-to     { opacity: 0; transform: translateY(-6px); }

.sv__chip {
  flex-shrink: 0;
  padding: 6px 14px; border-radius: var(--radius-full);
  background: rgba(26, 22, 18, 0.55);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(247, 241, 230, 0.16);
  color: #F7F1E6; font-family: var(--font-primary);
  font-size: 0.78rem; font-weight: 700; cursor: pointer;
  white-space: nowrap; transition: all 0.15s;
  &--on {
    background: var(--color-signal, #E2B673); color: #1a1410;
    border-color: var(--color-signal, #E2B673);
  }
  &--off { opacity: 0.4; }
  &:disabled { cursor: not-allowed; }
}

// ── Feed (vertical snap-scroll container) ─────
.sv__feed {
  position: absolute; inset: 0;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

// ── Card (fills the scroll container exactly) ──
.sv__card {
  position: relative;
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  overflow: hidden;
  background: #0b0907;
}

// Image zone — fills the card; tap opens lightbox
.sv__card-image {
  position: absolute; inset: 0;
  cursor: zoom-in;
}

.sv__card-img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}

// Bottom gradient + info — sits at the bottom of the card.
.sv__card-overlay {
  position: absolute;
  left: 0; right: 0;
  bottom: 0;
  padding-bottom: 16px;
  padding-top: 48px;
  pointer-events: none;
  transition: opacity 0.25s ease;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.55) 40%,
    rgba(0, 0, 0, 0.0)  100%
  );
}

.sv__card-info {
  pointer-events: auto;
  cursor: pointer;
  padding: 0 var(--page-padding-x);
  display: flex; flex-direction: column; gap: 6px;
  color: #F7F1E6;
}

// Name + meta share one wrapping row — meta pills sit inline after the name
.sv__card-head {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 10px;
}

.sv__card-name {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.2;
  color: #F7F1E6;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  flex: 1; min-width: 0;
}

.sv__card-meta {
  display: flex; align-items: center; gap: 8px;
  flex-shrink: 0;
}

.sv__card-like {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px;
  border-radius: var(--radius-full);
  background: rgba(26, 22, 18, 0.45);
  border: 1px solid rgba(247, 241, 230, 0.22);
  color: rgba(247, 241, 230, 0.85);
  font-family: var(--font-mono);
  font-size: 0.72rem; font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  i { font-size: 0.78rem; }

  &--on {
    background: rgba(216, 101, 69, 0.25);
    border-color: rgba(216, 101, 69, 0.55);
    color: #f0a58e;
    i { color: var(--color-terra, #D86545); }
  }
}

.sv__card-flag {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px;
  border-radius: 50%;
  background: rgba(26, 22, 18, 0.45);
  border: 1px solid rgba(247, 241, 230, 0.18);
  color: rgba(247, 241, 230, 0.5);
  font-size: 0.62rem;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: #f0a58e; border-color: rgba(216, 101, 69, 0.55); }
}

.sv__card-visit {
  display: inline-flex; align-items: center; gap: 4px;
  font-family: var(--font-mono);
  font-size: 0.68rem; font-weight: 700;
  padding: 3px 9px;
  border-radius: var(--radius-full);
  border: 1px solid;
  white-space: nowrap;

  &--visit {
    background: rgba(143, 163, 122, 0.20);
    color: #b8d4a4;
    border-color: rgba(143, 163, 122, 0.40);
  }
  &--pickup {
    background: rgba(226, 182, 115, 0.20);
    color: #f0d3a0;
    border-color: rgba(226, 182, 115, 0.40);
  }
  &--delivery {
    background: rgba(122, 76, 102, 0.25);
    color: #e0a8c4;
    border-color: rgba(122, 76, 102, 0.45);
  }

  i { font-size: 0.62rem; }
}

.sv__card-rating {
  display: inline-flex; align-items: center; gap: 4px;
  font-family: var(--font-mono);
  font-size: 0.78rem; font-weight: 700;
  color: #f5a623;
  i { font-size: 0.72rem; }
}

.sv__card-price {
  font-family: var(--font-mono);
  font-size: 0.78rem; font-weight: 700;
  color: #1a1410;
  background: var(--color-signal);
  padding: 3px 10px;
  border-radius: var(--radius-full);
}

// Clamped to 2 lines; tap toggles the full text (ellipsis signals there's more)
.sv__card-review {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: rgba(247, 241, 230, 0.92);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;

  &--open {
    display: block;
    -webkit-line-clamp: unset;
  }
}

.sv__card-by {
  color: rgba(247, 241, 230, 0.55);
  font-size: 0.78rem;
}

// Single tappable line: pin · branch name · district — no boxed panel
.sv__card-place {
  display: flex; align-items: center; gap: 8px;
  align-self: stretch;
  padding: 4px 0;
  color: #F7F1E6;

  > i { font-size: 0.78rem; color: var(--color-signal); flex-shrink: 0; }
}

// line-height ≥1.5 on these ellipsis boxes: tighter clips the dots of a
// final ي (Arabic descenders hang below the line box)
.sv__card-place-name {
  font-family: var(--font-display);
  font-size: 0.9rem;
  font-weight: 800;
  color: #F7F1E6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  flex-shrink: 1; min-width: 0;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
}

.sv__card-place-loc {
  font-size: 0.72rem;
  color: rgba(247, 241, 230, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  flex-shrink: 2; min-width: 0;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
}

.sv__card-place-arrow {
  font-size: 0.70rem !important;
  color: rgba(247, 241, 230, 0.45) !important;
  flex-shrink: 0;
  margin-inline-start: auto; // push to the far (left in RTL) edge
}

// ── Loading skeleton (under each image until decoded) ──
.sv__card-skel {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background:
    linear-gradient(
      135deg,
      rgba(26, 22, 18, 1) 0%,
      rgba(34, 28, 23, 1) 50%,
      rgba(26, 22, 18, 1) 100%
    );

  i {
    font-size: 1.4rem;
    color: rgba(226, 182, 115, 0.45);
  }
}

// ── States (loading / empty / end) ────────────
.sv__state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px;
  padding: var(--space-6) var(--page-padding-x);
  color: rgba(247, 241, 230, 0.55);
  font-family: var(--font-primary);
  font-size: 0.88rem;
  text-align: center;

  i.fa-spinner { font-size: 1.4rem; color: var(--color-signal); }
}

.sv__state--empty {
  height: 100%;
  scroll-snap-align: start;
  background: var(--color-bg);

  p { margin: 0; font-size: 1rem; font-weight: 700; color: var(--color-text-2); }
  span { font-size: 0.82rem; color: var(--color-text-4); }
}

.sv__state-icon { font-size: 3rem; }

.sv__state--more {
  padding: var(--space-4);
}

// ── Fullscreen lightbox ───────────────────────
// Teleported to <body>, sits above AppFooter and everything else.
.sv__lb {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.96);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  touch-action: none;
}

.sv__lb-close {
  position: absolute;
  top: max(16px, env(safe-area-inset-top, 16px));
  left: 16px;
  z-index: 1;
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(247, 241, 230, 0.10);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(247, 241, 230, 0.20);
  color: #F7F1E6;
  font-size: 1rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: rgba(247, 241, 230, 0.18); }
}

.sv__lb-img {
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  transition: transform 0.2s cubic-bezier(0.32, 0.72, 0, 1);
  transform-origin: center center;
  will-change: transform;

  &--dragging { transition: none; }
}

// notifications
.sv__avatar { position: relative; }   // if not already
.sv__avatar-dot {
  position: absolute; top: -2px; left: -2px;   // left = visual leading corner in RTL
  width: 12px; height: 12px; border-radius: 50%;
  background: var(--color-closed, #D86545);
  border: 2px solid var(--color-bg);
}

// Lightbox fade in/out
.lb-fade-enter-active,
.lb-fade-leave-active { transition: opacity 0.18s ease; }
.lb-fade-enter-from,
.lb-fade-leave-to     { opacity: 0; }
</style>